import { Component, computed, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationService } from '@selfCareShared/services/notification.service';
import { NzBadgeComponent } from 'ng-zorro-antd/badge';
import { NzDrawerComponent, NzDrawerContentDirective } from 'ng-zorro-antd/drawer';
import { NzDropDownDirective, NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzHeaderComponent } from 'ng-zorro-antd/layout';
import { NzMenuDirective, NzMenuDividerDirective, NzMenuItemComponent } from 'ng-zorro-antd/menu';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { catchError, finalize, forkJoin, of, take } from 'rxjs';
import { NavigateService, UtilsService } from 'shared-lib';
import { NotificationContentComponent } from '../notification/notification-content.component';
import { PushNotificationService } from '../../../shared/services/push-notification.service';
import { AuthService } from '../../../core/auth/auth.service';
import { UserService } from '../../../core/user';
import { sortBy } from 'lodash';
import { AppTranslationService } from '@selfCareCore/translation/translation.service';
import { LanguageSelectionComponent } from '@selfCareShared/components/language-selection/language-selection.component';
import { resolveMediaUrl } from '../../../shared/utils/resolve-media-url.util';
import { environment } from 'projects/self-care/src/environments/environment';

@Component({
  selector: 'race-header',
  imports: [
    TranslatePipe,
    NzDrawerComponent,
    NzDropdownMenuComponent,
    NotificationContentComponent,
    NzBadgeComponent,
    NzIconDirective,
    NzDropDownDirective,
    NzMenuDirective,
    NzMenuItemComponent,
    NzTooltipDirective,
    NzMenuDividerDirective,
    RouterLink,
    NzDrawerContentDirective,
    NzHeaderComponent,
    LanguageSelectionComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  readonly collapsed = input(false);
  readonly logoPath = environment.logoPath;
  readonly collapse = output<void>();
  readonly roles = computed(() => {
    return this.user()?.roles || [];
  });
  readonly profileImageLoadError = signal<boolean>(false);
  readonly profileImage = computed(() => {
    if (this.profileImageLoadError()) {
      return null;
    }
    const profileImagePath = this.userService.user()?.profileImage;
    return profileImagePath ? resolveMediaUrl(profileImagePath) : null;
  });
  selectedRoleName: string = localStorage.getItem('selectedRoleName') || '';
  selectedRoleId: string = localStorage.getItem('selectedRoleId') || '';
  private router = inject(Router);
  private auth = inject(AuthService);
  private navigationService = inject(NavigateService);
  private utilsService = inject(UtilsService);
  protected appTranslationService = inject(AppTranslationService);
  protected readonly userService = inject(UserService);
  readonly notificationService = inject(NotificationService);
  private readonly pushNotificationService = inject(PushNotificationService);

  notificationCount = 0;
  user: any = signal<any>({});
  isVisible = false;
  visible = false;
  isDrawerLoading = false;
  requestNotificationList: any[] = [];
  notificationList: any[] = [];

  constructor() {
    effect(() => {
      const profileImagePath = this.userService.user()?.profileImage;
      if (profileImagePath) {
        this.profileImageLoadError.set(false);
      }
    });
  }

  onProfileImageError(): void {
    this.profileImageLoadError.set(true);
  }

  ngOnInit(): void {
    this.pushNotificationService.initializePushNotifications();

    void this.notificationService.getUnreadCount();

    this.getBellNotifications();
  }

  changeRole(roleId: string, roleName: string): void {
    this.setRole({ roleId, roleName });
  }

  logout(): void {
    this.auth.logout();
    this.navigationService.toUrl('/auth/login');
  }

  changeCollapse(): void {
    this.collapse.emit();
  }

  open(): void {
    this.visible = true;
    this.getBellNotifications();
  }

  close($event): void {
    this.visible = !$event;
  }

  fullName(user: any): string {
    return `${user.firstName ? user.firstName : ''} ${user.lastName ? user.lastName : ''}`;
  }

  closeModal(): void {
    this.isVisible = false;
  }

  getBellNotifications(): void {
    this.isDrawerLoading = true;

    forkJoin({
      unreadCount: this.notificationService.getUnread({ sortBy: 'id;DESC' }).pipe(
        catchError((error) => {
          console.error('Error fetching unread count:', error);
          return of({ total: 0 });
        })
      ),
      notifications: this.notificationService.getAll({ unread: true, sortBy: 'id;DESC' }).pipe(
        catchError((error) => {
          console.error('Error fetching notifications:', error);
          return of({ data: [] });
        })
      ),
    })
      .pipe(
        take(1),
        finalize(() => {
          this.isDrawerLoading = false;
        })
      )
      .subscribe({
        next: ({ unreadCount, notifications }: any) => {
          this.notificationService.notificationCount.set(this.resolveUnreadCount(unreadCount));
          this.notificationService.notificationList.set(notifications?.data || []);
          this.notificationList = notifications?.data || [];
        },
        error: (error) => {
          console.error('Error fetching notifications:', error);
          this.utilsService.showError('Error fetching notifications', error);
        },
      });
  }

  private resolveUnreadCount(response: unknown): number {
    if (typeof response === 'number') {
      return response;
    }

    if (response && typeof response === 'object') {
      const unreadResponse = response as { total?: unknown; data?: unknown; count?: unknown };
      const candidate = unreadResponse.total ?? unreadResponse.data ?? unreadResponse.count;
      if (typeof candidate === 'number') {
        return candidate;
      }
      if (typeof candidate === 'string') {
        const parsedCount = Number(candidate);
        return Number.isFinite(parsedCount) ? parsedCount : 0;
      }
    }

    return 0;
  }

  notificationAction(notification: any): void {
    if (notification.actionPathWeb) {
      this.router.navigate([notification?.actionPathWeb]).then();
      this.visible = false;
    } else {
      this.isVisible = true;
    }
  }

  setRole(data: any): void {
    this.selectedRoleName = data.roleName;
    this.selectedRoleId = data.roleId;
    localStorage.setItem('selectedRoleName', data.roleName);
    localStorage.setItem('selectedRoleId', data.roleId);
  }
}
